import {
  DATETIME_FORMAT,
  DATE_FORMAT,
  DEFAULT_APP_NAME,
  DEFAULT_EMPTY,
  DELAY_TRANSITION,
  IMAGE_URL,
} from 'constants/common';
import _, { isNull, isUndefined } from 'lodash';
import moment from 'moment';
import i18n from './i18n';
import { LocalStorage, STORAGE_KEY } from './localStorage';
import { v4 as uuidv4 } from 'uuid';
import { AppConfigType } from 'types/property.type';
import { getBranchConfig } from 'services/property.service';

const Storage = new LocalStorage();

export const showData = (data: any) => {
  if (isNull(data) || isUndefined(data) || data.length === 0) return DEFAULT_EMPTY;
  else return data;
};

export const formatPrice = (price: number) => {
  return (
    price
      .toLocaleString('it-IT', { style: 'currency', currency: 'vnd' })
      .replace(/[^0-9\.-]+/g, '') + 'đ'
  );
};

export const isSuccessCode = (code?: number) => {
  return code ? String(code).length === 4 : false;
};

export const disableAllInput = () => {
  const listInput = document.getElementsByTagName('input');
  for (let i = 0; i < listInput.length; i++) {
    const input = listInput[i] as HTMLInputElement;
    input.disabled = true;
  }
};

export const enableAllInput = () => {
  const listInput = document.getElementsByTagName('input');
  for (let i = 0; i < listInput.length; i++) {
    const input = listInput[i] as HTMLInputElement;
    input.disabled = false;
  }
};

export const delayNavigate = (callback: () => any) => {
  setTimeout(() => {
    callback();
  }, DELAY_TRANSITION);
};

export const concatFullName = (firstName: string, lastName: string) => {
  return `${firstName} ${lastName}`;
};

export const formatDate = (date?: string) => {
  return date ? moment(date).format(DATE_FORMAT) : '';
};

export const formatDateTime = (date?: string) => {
  return date ? moment(date).format(DATETIME_FORMAT) : '';
};

export const formatDateTimeNow = (date?: string) => {
  const now = moment();
  const inputDate = moment(date);
  if (now.get('day') - inputDate.get('day') === 0) {
    if (i18n.language === 'vi') inputDate.locale('vi');
    else {
      inputDate.locale('en-us');
    }

    return date ? inputDate.fromNow() : '';
  } else return date ? moment(date).format(DATETIME_FORMAT) : '';
};

export const isNowDay = (date?: string) => {
  const now = moment();
  const inputDate = moment(date);

  return now.get('day') - inputDate.get('day') === 0;
};

export const convertToHTMLWithSup = (inputString: string) => {
  const outputString = inputString.replace(/m\^(\d)/g, 'm<sup>$1</sup>');

  return outputString;
};

export const getAvatarUrl = () => {
  const avatarUrl =
    Storage.getStorageItem(STORAGE_KEY.ACCOUNT_INFO)?.accountDetail?.avatarUrl || '';

  return `${IMAGE_URL}?path=${avatarUrl}`;
};

export const getImageURL = (url: string) => {
  return `${IMAGE_URL}?path=${url}`;
};

export const fileToBase64 = (file: File) => {
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = function () {
      const base64String = String(reader?.result);

      resolve(base64String || '');
    };

    reader.readAsDataURL(file);
  });
};

export const getMimeTypeFromBase64 = (base64: string) => {
  // RegExp to extract the MIME type part of the Base64 string
  const result = base64.match(/^data:(.*?);base64,/);

  if (result && result.length > 1) {
    return result[1];
  } else {
    return null; // or assume a default type, or throw an error, etc.
  }
};

export const getVersion = () => Storage.getStorageItem(STORAGE_KEY.APP_VERSION);

//NOTE: display-mode chỉ thành 'standalone' sau khi Chrome đọc xong manifest. Ngay lần đầu
//cài lại app, bundle JS có thể chạy trước thời điểm đó nên matchMedia trả false dù đang ở
//trong app đã cài -> app hiện InstallOverlay rỗng (Android không bắn beforeinstallprompt
//cho app đã cài) -> trắng màn hình. Vì vậy phải xét thêm các dấu hiệu khác.
const isStandaloneDisplay = () => {
  try {
    //NOTE: Chrome dùng standalone, một số máy Android mở qua WebAPK báo minimal-ui/fullscreen
    return ['standalone', 'minimal-ui', 'fullscreen'].some(
      (mode) => window.matchMedia(`(display-mode: ${mode})`).matches,
    );
  } catch {
    return false;
  }
};

//NOTE: iOS Safari không hỗ trợ display-mode cho app đã Add to Home Screen, chỉ có cờ riêng này
const isIOSStandalone = () => (window.navigator as any)?.standalone === true;

//NOTE: Khi mở từ WebAPK trên Android, referrer có dạng android-app://...
const isAndroidApp = () => {
  try {
    return document.referrer.startsWith('android-app://');
  } catch {
    return false;
  }
};

export const isPWA = () => {
  if (process.env.REACT_APP_DEV_MODE === 'true') return true;

  const standalone = isStandaloneDisplay() || isIOSStandalone() || isAndroidApp();

  //NOTE: Đã từng vào được chế độ standalone thì ghi nhớ lại. Lần sau nếu manifest chưa kịp
  //đọc xong, vẫn render App thay vì rơi về màn hình cài đặt.
  if (standalone) {
    Storage.setStorageItem(STORAGE_KEY.IS_PWA_INSTALLED, true);

    return true;
  }

  return Boolean(Storage.getStorageItem(STORAGE_KEY.IS_PWA_INSTALLED));
};

export const generateUUID = () => {
  return uuidv4();
};

//NOTE: Chờ tối đa 5s cho config thương hiệu, quá thì render bằng fallback rồi thôi
const INIT_CONFIG_TIMEOUT = 5000;

const FALLBACK_APP_CONFIG: AppConfigType = {
  zaloId: '',
  appDomain: 'localhost',
  appName: 'MORA',
  appLogo: 'https://raw.githubusercontent.com/The-New-Tech/apartech-document/staging/logo.svg',
};

export const initConfigApp = async () => {
  const domain = window.location.hostname;
  const existedConfig = Storage.getStorageItem(STORAGE_KEY.APP_CONFIG);
  let configApp: AppConfigType = FALLBACK_APP_CONFIG;

  try {
    //NOTE: Bước này chặn render nên bắt buộc phải có timeout. Nếu API treo (mạng chập chờn,
    //máy vừa cài lại chưa có config cũ trong localStorage) thì await sẽ treo vô hạn và
    //người dùng nhìn thấy trang trắng vĩnh viễn.
    const response = await Promise.race([
      getBranchConfig(domain),
      new Promise<never>((_resolve, reject) =>
        setTimeout(() => reject(new Error('init-config-timeout')), INIT_CONFIG_TIMEOUT),
      ),
    ]);
    const { data } = response.data;
    if (!_.isEmpty(data)) configApp = data;
  } catch (error) {
    //NOTE: API lỗi thì dùng config đã lưu hoặc fallback, không được để app trắng màn hình
    if (!_.isEmpty(existedConfig)) configApp = existedConfig;
  }

  Storage.setStorageItem(STORAGE_KEY.APP_CONFIG, configApp);
  const appName = document.getElementById('app-name');
  if (appName) appName.innerText = configApp.appName || DEFAULT_APP_NAME;
};

export const getAppName = () => {
  const config = Storage.getStorageItem(STORAGE_KEY.APP_CONFIG) as AppConfigType;

  return !_.isEmpty(config) ? String(config?.appName) : DEFAULT_APP_NAME;
};

export const getAppLogo = () => {
  const host = window.location.host;
  const appLogo =
    'https://raw.githubusercontent.com/The-New-Tech/apartech-document/refs/heads/staging/{domain-holder}/logo.png';

  // Thay thế tên miền trong URL logo nếu cần
  return appLogo.replace('{domain-holder}', host);
};

export const getFileUrl = (url: string) => {
  return `${IMAGE_URL}?path=${url}`;
};
